import React, { useCallback, useContext, useMemo, useState } from 'react'
import { selectCurrentDocument } from '../../redux/application-slice'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectTextSelected,
  setEditRedlineComment,
  setSelectedBlacklineSegment,
  setTextSelected,
} from '../../redux/viewer-slice'
import { ArrowPathIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline'
import { POSTHOG } from '../../utils/posthog-constants'
import { usePostHog } from 'posthog-js/react'
import { Tooltip } from 'react-tooltip'
import { Quads } from '../../shared/interfaces/quads.interface'
import { DocumentViewerContext } from '../../contexts/document-viewer-instance-context'
import { RevisionSegment } from '../../shared/interfaces/project/document/revision/revision.interface'
import {
  useCreateBlacklineSegmentMutation,
  useGetDocumentBlacklineQuery,
  useGetUserProfileQuery,
} from '../../redux/api-slice'
import { skipToken } from '@reduxjs/toolkit/query'

interface ContextMenuRedlineProps {
  positionX: string
  positionY: string
}

const ContextMenuRedline: React.FC<ContextMenuRedlineProps> = ({
  positionX,
  positionY,
}) => {
  const currentDocument = useSelector(selectCurrentDocument)
  const { currentData: documentBlacklines } = useGetDocumentBlacklineQuery(
    currentDocument?.id ? currentDocument?.id : skipToken
  )
  const textSelected = useSelector(selectTextSelected)
  const dispatch = useDispatch()
  const posthog = usePostHog()
  const documentViewerContext = useContext(DocumentViewerContext)
  const { documentViewer } = documentViewerContext
  const [commentSaving, setCommentSaving] = useState(false)
  const [createBlacklineSegment] = useCreateBlacklineSegmentMutation()
  const { currentData: currentUser } = useGetUserProfileQuery()
  const documentBlackline = useMemo(() => {
    if (!documentBlacklines) {
      return null
    }
    return documentBlacklines.find(
      (b) => b.document?.id === currentDocument?.id && b?.blackline_type === 2
    )
  }, [documentBlacklines, currentDocument?.id])

  const onCreateCommentClicked = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation()
      e.preventDefault()
      setCommentSaving(true)
      if (!currentDocument?.id) {
        return
      }
      const text = textSelected?.text?.replace(/(\r\n|\n|\r)/gm, ' ') ?? ''
      posthog?.capture(POSTHOG.comment_created_opened, {
        document_uuid: currentDocument?.uuid,
      })
      if (!textSelected || !textSelected.pageNumber || !documentBlackline?.id) {
        return
      }
      const height = documentViewer?.getPageHeight(textSelected.pageNumber)
      const width = documentViewer?.getPageWidth(textSelected.pageNumber)
      if (!height || !width) {
        return
      }
      const blacklineSegments: RevisionSegment[] = []
      for (const quadPage in textSelected.quads) {
        const quads: Quads[] = textSelected.quads[quadPage].map(
          (quad: Quads) => {
            return {
              x1: quad.x1 / width,
              x2: quad.x2 / width,
              x3: quad.x3 / width,
              x4: quad.x4 / width,
              y1: quad.y1 / height,
              y2: quad.y2 / height,
              y3: quad.y3 / height,
              y4: quad.y4 / height,
              page: parseInt(quadPage),
            }
          }
        )
        blacklineSegments.push({
          page: parseInt(quadPage),
          quads,
          document: currentDocument?.id,
          text: textSelected.text ?? '',
        })
      }
      const response = await createBlacklineSegment({
        page: textSelected.pageNumber,
        quads: blacklineSegments?.[0]?.quads ?? [],
        text: text,
        match_id: '-1',
        match_type: 0,
        document_blackline: documentBlackline?.id,
        user_created: currentUser,
        document_segment: {
          document: currentDocument?.v1_document,
          page: textSelected.pageNumber,
          text: text,
        },
        // Add the section reference in here
        // section_reference: localRevision?.section_reference,
      }).unwrap()
      posthog?.capture(POSTHOG.blackline_comment_created, {
        document_uuid: currentDocument?.uuid,
        comment_id: response?.comments?.[0]?.id,
      })
      dispatch(setEditRedlineComment(response?.comments?.[0]?.id ?? null))
      dispatch(setTextSelected(null))
      dispatch(setSelectedBlacklineSegment(response))
    },
    [
      currentDocument?.id,
      currentDocument?.uuid,
      textSelected,
      posthog,
      documentViewer,
      createBlacklineSegment,
      dispatch,
      documentBlackline?.id,
      currentDocument?.v1_document,
      currentUser,
    ]
  )

  return (
    <div
      className={'absolute z-50 flex rounded border bg-white shadow-lg'}
      style={{
        top: positionY,
        left: positionX,
      }}
    >
      <button
        type={'button'}
        className={
          'z-50 flex h-8 w-8 cursor-pointer items-center justify-center  rounded hover:bg-gray-100'
        }
        onMouseDown={onCreateCommentClicked}
        data-tooltip-id="comment-id"
        data-tooltip-content={commentSaving ? 'Saving...' : 'Create Comment'}
        disabled={commentSaving}
        aria-label="Create Comment"
      >
        {commentSaving ? (
          <ArrowPathIcon width={20} className="animate-spin" />
        ) : (
          <ChatBubbleLeftIcon width={20} />
        )}
        <Tooltip id={'comment-id'} style={{ zIndex: 100 }} />
      </button>
    </div>
  )
}

export default ContextMenuRedline
